import React, { useMemo, useEffect } from 'react'
import { useSpring, animated, useSpringRef, useChain } from '@react-spring/web'
import { styled } from 'styled-components'
import {
	useAnalyticsQuestionPercent,
	useLocalDispatch,
	startLocalSoundEffect,
	endLocalSoundEffect,
} from '../../../context'
import { SPECIAL_EFFECT_IDS as SOUNDS } from '@mission.io/mission-toolkit/constants'
import type { SharedProps } from './types'
import {
	EOMS_BACKGROUND,
	EOMS_BORDER,
	EOMS_BORDER_RADIUS,
	EOMS_DARK_HIGHLIGHT,
} from './styles'
import { cssPlayerHeightUnit } from '../../../utility/constants'
import {
	QUESTION_COUNT_UP_DELAY,
	getQuestionCountUpDuration,
} from './durations'

/**
 * QuestionsAnsweredSelectingComponent - Displays the animated Question End Of Mission Sequence component. Sets up the props and
 *    guards against rerenders.
 *
 *
 * @param  {Object} props - the react props
 * @param  {started} props.started - true if the animation should or should have already started
 * @param  {() => void} props.onFinish - a call back for when the animations finishes
 *
 * @returns React$Node
 */
export default function QuestionsAnsweredSelectingComponent(
	props: SharedProps
): React$Node {
	const percentValue: ?number = useAnalyticsQuestionPercent()
	return useMemo(() => {
		return <QuestionsAnsweredComponent {...props} percentValue={percentValue} />
	}, [props, percentValue])
}

/**
 * QuestionsAnsweredComponent - animated top score component for the End Of Mission Sequence which counts up the student's scores.
 *    You should guard against rerendering this component so that the animations work correctly.
 *
 * @param  {Object} props - the react props
 * @param  {started} props.started - true if the animation should or should have already started
 * @param  {() => void} props.onFinish - a call back for when the animations finishes
 * @param  {?number} props.percentValue - the percent of correct answers during the mission
 *
 * @returns React$Node
 */
function QuestionsAnsweredComponent({
	percentValue,
	started,
	onFinish,
}: {
	percentValue: ?number,
	...SharedProps,
}): React$Node {
	const percentRef = useSpringRef()
	const dispatch = useLocalDispatch()

	const percentProps = useSpring({
		delay: QUESTION_COUNT_UP_DELAY,
		to: { percent: percentValue },
		from: { percent: 0 },
		config: { duration: getQuestionCountUpDuration(percentValue || 0) },
		onStart: () => {
			dispatch(startLocalSoundEffect(SOUNDS.EOMS_COUNT_UP_CLICKS))
			dispatch(startLocalSoundEffect(SOUNDS.EOMS_SUSPENSE))
		},
		onRest: () => {
			dispatch(endLocalSoundEffect(SOUNDS.EOMS_COUNT_UP_CLICKS))
			dispatch(endLocalSoundEffect(SOUNDS.EOMS_SUSPENSE))
			dispatch(startLocalSoundEffect(SOUNDS.EOMS_SUCCESS_SUPER_SPARKLE))
			onFinish()
		},
		ref: percentRef,
	})

	useEffect(() => {
		if (started && !percentValue) {
			dispatch(startLocalSoundEffect(SOUNDS.EOMS_SUCCESS_SUPER_SPARKLE))
			onFinish()
		}
	}, [started, percentValue, onFinish, dispatch])

	useChain(started ? [percentRef] : [])

	return (
		<CenterOnPage>
			<Container flex center>
				<Body>
					<Text>Questions Correct</Text>
					<DataAligner>
						<Data>
							{percentValue == null
								? 'N / A'
								: percentProps.percent.interpolate(val => `${val.toFixed()}%`)}
						</Data>
					</DataAligner>
				</Body>
			</Container>
		</CenterOnPage>
	)
}

const Container = styled.div`
	background-color: ${EOMS_BACKGROUND};
	width: 80%;
	border: ${EOMS_BORDER};
	border-radius: ${EOMS_BORDER_RADIUS};
`

const Body = styled.div`
	text-align: center;
`

const Data = styled(animated.div)`
	font-size: ${cssPlayerHeightUnit(6)};
	margin: ${cssPlayerHeightUnit(6.5)};
	padding: ${cssPlayerHeightUnit(1.5)};
	min-width: 30%;
	background-color: ${EOMS_DARK_HIGHLIGHT};
	border-radius: ${EOMS_BORDER_RADIUS};
`

const DataAligner = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
`

const Text = styled.div`
	font-size: ${cssPlayerHeightUnit(5)};
	border-bottom: ${EOMS_BORDER};
	padding: ${cssPlayerHeightUnit(3.5)};
`

const CenterOnPage = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
`
